import React, {useId} from 'react';
import {AutocompleteProps} from '@mui/material';
import {Autocomplete as PathAutocomplete} from '@ellucian/react-design-system/core';
import {useField} from 'formik';

export type Props = Omit<
  AutocompleteProps<string, boolean, boolean, boolean>,
  'size' | 'renderInput'
> & {
  label: string;
  error?: boolean | string;
  size?: 'small' | 'default';
  placeholder: string;
  className?: string;
  cy?: string;
};

const EllucianAutocomplete = ({
  label,
  error,
  size = 'default',
  cy,
  className,
  ...autocompleteProps
}: Props) => {
  const errorId = useId();
  const hasError = !!error;

  return (
    <div>
      <PathAutocomplete
        {...autocompleteProps}
        className={className}
        label={label}
        size={size}
        error={hasError}
        sx={{'& .MuiAutocomplete-input': {height: 20}}}
        inputProps={{
          'data-testid': cy,
          'data-cy': cy,
          ...(hasError && {
            'aria-invalid': true,
            'aria-describedby': errorId,
          }),
        }}
      />
      {hasError && (
        <div
          id={errorId}
          className="shared-error-text"
          role="alert"
          data-testid="shared-text-input-error"
        >
          {error}
        </div>
      )}
    </div>
  );
};

export const FormikEllucianAutocomplete = (props: {name: string} & Props) => {
  const [field, meta] = useField({name: props.name, type: 'text'});

  return (
    <EllucianAutocomplete {...props} {...field} error={meta.touched ? meta.error : undefined} />
  );
};

export default EllucianAutocomplete;
